import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField, Theme, ThemeOptions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import MultiCheckbox from 'src/components/MultiCheckBox';
import LicenseContext from 'src/contexts/LicenseContext';
import { PartnerCompanyEntity, PartnerCompanyRequestEntity } from 'src/entities/PartnerCompanyEntity';
import { queryKey, useMutationPartnerCompanies, usePartnerCompanyMastersRequest } from 'src/hooks/usePartnerCompanies.request';

import { schema } from './schema';

type Props = {
  open: boolean;
  onClose: () => void;
  entity?: PartnerCompanyEntity;
}

const FormDialog:FC<Props> = memo((
  {
    open,
    onClose,
    entity,
  }
) => {
  const theme: Theme & ThemeOptions = useTheme();
  const licenseContext = useContext(LicenseContext);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [truckKlasses, setTruckKlasses] = useState<string[]>(entity?.truckKlasses ?? []);
  const [tempretureZones, setTempretureZones] = useState<string[]>(entity?.tempretureZones ?? []);
  const [serviceAreas, setServiceAreas] = useState<string[]>(entity?.serviceAreas ?? []);

  const { addPartnerCompany, updatePartnerCompany } = useMutationPartnerCompanies();
  const { data: master } = usePartnerCompanyMastersRequest();

  const form = useForm<(PartnerCompanyRequestEntity | PartnerCompanyEntity)>({
    resolver: yupResolver(schema),
  });

  const {
    control,
    setValue,
    getValues,
    reset,
    register,
    handleSubmit,
    formState: { errors }
  } = form;

  const onSubmitAdd: SubmitHandler<PartnerCompanyRequestEntity | PartnerCompanyEntity> = async (
    data
  ): Promise<void> => {
    try {
      data.truckKlasses = truckKlasses;
      data.tempretureZones = tempretureZones;
      data.serviceAreas = serviceAreas;

      if ('id' in data) {
        await updatePartnerCompany.mutateAsync(data);
      } else {
        await addPartnerCompany.mutateAsync(data);
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-argument
      error.response.data.map((str) => enqueueSnackbar(str));
    } finally {
      await queryClient.invalidateQueries({
        queryKey: [queryKey]
      });
      reset();
      onClose();
    }
  };

  const closeDialog = () => {
    onClose();
    reset();
  };

  const haveSelectableCompanies = licenseContext?.config?.selectable_companies?.length > 1;
  const isNew = !entity;

  useEffect(() => {
    if (entity) {
      reset(entity);
      setTruckKlasses(entity.truckKlasses ?? []);
      setTempretureZones(entity.tempretureZones ?? []);
      setServiceAreas(entity.serviceAreas ?? []);
      return;
    }

    reset({ companyId: licenseContext?.config?.selected_company_id });
    setTruckKlasses([]);
    setTempretureZones([]);
    setServiceAreas([]);
  }, [entity, reset, open, licenseContext?.config?.selected_company_id]);

  useEffect(() => {
    // 編集時 (entity あり) は 1 番目の useEffect が reset(entity) で値を復元するため、
    // ここで companyId だけの reset を走らせると entity の値 (hierarchyLevel 等) を消してしまう。
    // 新規作成時のみ選択中の会社で初期化する。
    if (entity) return;
    if (!licenseContext?.config?.selected_company_id) return;
    if (licenseContext?.config?.selected_company_id === 0) {
      reset({ companyId: 0 });
      return;
    }

    reset({ companyId: licenseContext?.config?.selected_company_id });
    setTruckKlasses([]);
    setTempretureZones([]);
    setServiceAreas([]);
  }, [entity, licenseContext?.config?.selected_company_id, reset]);

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        運送会社
        {isNew ? '作成' : '編集'}
      </DialogTitle>
      <DialogContent>
        <Stack component="form" noValidate spacing={2}>
          {[haveSelectableCompanies, isNew].every((bool) => bool) && (
            <FormControl
              fullWidth
              size="small"
              sx={{
                marginTop: theme.spacing(1),
              }}
            >
              <InputLabel id="company_id_label" sx={{ pt: 1, ml: -1.5 }}>事業所</InputLabel>
              <Controller
                control={control}
                name="companyId"
                render={({ field }) => (
                  <Select
                    id="companyId"
                    margin="dense"
                    label="事業所"
                    type="number"
                    fullWidth
                    variant="standard"
                    value={`${getValues('companyId')}`}
                    onChange={(e) => setValue('companyId', Number(e.target.value))}
                    disabled={!haveSelectableCompanies}
                  >
                    {licenseContext?.config?.selectable_companies?.map((company) => (
                      <MenuItem value={company.id} key={company.id}>{company.name}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          )}
          <TextField
            required
            margin="dense"
            id="name"
            label="会社名"
            type="text"
            fullWidth
            variant="standard"
            {...register('name')}
            error={'name' in errors}
            helperText={errors.name?.message}
          />
          <TextField
            margin="dense"
            id="base"
            label="拠点名"
            type="text"
            fullWidth
            variant="standard"
            {...register('base')}
            error={'base' in errors}
            helperText={errors.base?.message}
          />
          <TextField
            margin="dense"
            id="address"
            label="拠点住所"
            type="text"
            fullWidth
            variant="standard"
            {...register('address')}
            error={'address' in errors}
            helperText={errors.address?.message}
          />
          <TextField
            margin="dense"
            id="contactPerson"
            label="担当者"
            type="text"
            fullWidth
            variant="standard"
            {...register('contactPerson')}
            error={'contactPerson' in errors}
            helperText={errors.contactPerson?.message}
          />
          <TextField
            margin="dense"
            id="emailAddress"
            label="メールアドレス"
            type="text"
            fullWidth
            variant="standard"
            {...register('emailAddress')}
            error={'emailAddress' in errors}
            helperText={errors.emailAddress?.message}
          />
          <TextField
            margin="dense"
            id="phoneNumber"
            label="電話番号"
            type="text"
            fullWidth
            variant="standard"
            {...register('phoneNumber')}
            error={'phoneNumber' in errors}
            helperText={errors.phoneNumber?.message}
          />
          <FormControl
            fullWidth
            variant="standard"
            size="small"
            sx={{
              marginTop: theme.spacing(1),
            }}
          >
            <Controller
              control={control}
              name="category"
              render={({ field }) => (
                <>
                  <InputLabel>カテゴリ</InputLabel>
                  <Select
                    id="category"
                    margin="dense"
                    label="事業所"
                    type="number"
                    fullWidth
                    variant="standard"
                    value={`${getValues('category') ?? ''}`}
                    onChange={(e) => setValue('category', e.target.value)}
                  >
                    <MenuItem value="" key="none-category">&nbsp;</MenuItem>
                    {master.categories?.map((category) => (
                      <MenuItem value={category} key={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </>
              )}
            />
          </FormControl>
          <FormControl
            fullWidth
            variant="standard"
            size="small"
            sx={{
              marginTop: theme.spacing(1),
            }}
          >
            <Controller
              control={control}
              name="hierarchyLevel"
              render={(_field) => (
                <>
                  <InputLabel id="hierarchyLevel-label">請負階層</InputLabel>
                  <Select
                    labelId="hierarchyLevel-label"
                    id="hierarchyLevel"
                    margin="dense"
                    label="請負階層"
                    fullWidth
                    variant="standard"
                    value={`${getValues('hierarchyLevel') ?? ''}`}
                    onChange={(e) => setValue(
                      'hierarchyLevel',
                      String(e.target.value) === '' ? null : Number(e.target.value)
                    )}
                  >
                    <MenuItem value="" key="hierarchy-none">元請け</MenuItem>
                    <MenuItem value="1" key="hierarchy-1">1次請け</MenuItem>
                    <MenuItem value="2" key="hierarchy-2">2次請け</MenuItem>
                    <MenuItem value="3" key="hierarchy-3">3次請け</MenuItem>
                  </Select>
                </>
              )}
            />
          </FormControl>
          <FormControl
            fullWidth
            size="small"
            sx={{
              marginTop: theme.spacing(1),
            }}
          >
            <>
              <FormLabel component="legend">利用可能な車両タイプ</FormLabel>
              <FormGroup row>
                <MultiCheckbox<PartnerCompanyRequestEntity | PartnerCompanyEntity, string>
                  form={form}
                  control={control}
                  name="truckKlasses"
                  options={master?.truckKlasses}
                  defaultCheckedValues={truckKlasses}
                  valueSetter={setTruckKlasses}
                />
              </FormGroup>
            </>
          </FormControl>
          <FormControl
            fullWidth
            size="small"
            sx={{
              marginTop: theme.spacing(1),
            }}
          >
            <>
              <FormLabel component="legend">利用可能な温度帯</FormLabel>
              <FormGroup row>
                <MultiCheckbox<PartnerCompanyRequestEntity | PartnerCompanyEntity, string>
                  form={form}
                  control={control}
                  name="tempretureZones"
                  options={master?.tempretureZones}
                  defaultCheckedValues={tempretureZones}
                  valueSetter={setTempretureZones}
                />
              </FormGroup>
            </>
          </FormControl>
          <FormControl
            fullWidth
            size="small"
            sx={{
              marginTop: theme.spacing(1),
            }}
          >
            <>
              <FormLabel component="legend">対応エリア</FormLabel>
              <FormGroup row>
                <MultiCheckbox<PartnerCompanyRequestEntity | PartnerCompanyEntity, string>
                  form={form}
                  control={control}
                  name="serviceAreas"
                  options={master?.serviceAreas}
                  defaultCheckedValues={serviceAreas}
                  valueSetter={setServiceAreas}
                />
              </FormGroup>
            </>
          </FormControl>
          <TextField
            margin="dense"
            id="memo"
            label="メモ"
            type="text"
            fullWidth
            variant="standard"
            {...register('memo')}
            error={'memo' in errors}
            helperText={errors.memo?.message}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>キャンセル</Button>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button onClick={handleSubmit(onSubmitAdd)} variant="contained">
          保存する
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default FormDialog;
